import axios from "axios";
import { domain } from "../auth/domain";

const api = axios.create({
  baseURL: domain,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers.Authorization = `JWT ${token}`;
  }
  return config;
});

const UNIQUE_GROUP_NAME_PATTERN = /UNIQUE constraint failed:\s*profiles_vikobagroup\.name/i;
const HTML_ERROR_PATTERN = /<\s*(?:!doctype|html|head|body)\b/i;

const normalizeErrorMessage = (message, fallback) => {
  if (!message || typeof message !== "string") return fallback;
  if (UNIQUE_GROUP_NAME_PATTERN.test(message)) {
    return "Group name already exists. Use a different name.";
  }
  if (HTML_ERROR_PATTERN.test(message)) {
    return fallback;
  }
  return message;
};

const parseError = (error, fallback) => {
  if (error?.response?.data?.detail) {
    return normalizeErrorMessage(error.response.data.detail, fallback);
  }
  if (typeof error?.response?.data === "string") {
    return normalizeErrorMessage(error.response.data, fallback);
  }
  if (error?.response?.data) {
    try {
      return normalizeErrorMessage(
        Object.values(error.response.data).flat().join(" "),
        fallback
      );
    } catch {
      return fallback;
    }
  }
  return normalizeErrorMessage(error?.message, fallback);
};

export const listMigrations = async () => {
  try {
    const { data } = await api.get("migrations/");
    return data;
  } catch (error) {
    throw new Error(parseError(error, "Failed to load migrations."));
  }
};

export const getMigration = async (migrationId) => {
  try {
    const { data } = await api.get(`migrations/${migrationId}/`);
    return data;
  } catch (error) {
    throw new Error(parseError(error, "Failed to load migration."));
  }
};

export const createMigration = async (payload) => {
  try {
    const { data } = await api.post("migrations/", payload);
    return data;
  } catch (error) {
    throw new Error(parseError(error, "Failed to create migration."));
  }
};

export const fetchMigrationMembers = async (migrationId) => {
  try {
    const { data } = await api.get(`migrations/${migrationId}/members/`);
    return data;
  } catch (error) {
    throw new Error(parseError(error, "Failed to load members."));
  }
};

export const importMigrationMembers = async (migrationId, members) => {
  try {
    const { data } = await api.post(`migrations/${migrationId}/members/import/`, {
      members,
    });
    return data;
  } catch (error) {
    throw new Error(parseError(error, "Failed to import members."));
  }
};

export const importMigrationMembersFile = async (migrationId, file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await api.post(`migrations/${migrationId}/members/import/`, formData);
    return data;
  } catch (error) {
    throw new Error(parseError(error, "Failed to upload member file."));
  }
};

export const saveOpeningBalances = async (migrationId, balances) => {
  try {
    const { data } = await api.post(`migrations/${migrationId}/opening-balances/`, {
      balances,
    });
    return data;
  } catch (error) {
    throw new Error(parseError(error, "Failed to save opening balances."));
  }
};

export const saveSummaries = async (migrationId, summaries) => {
  try {
    const { data } = await api.post(`migrations/${migrationId}/summaries/`, {
      summaries,
    });
    return data;
  } catch (error) {
    throw new Error(parseError(error, "Failed to save historical summaries."));
  }
};

export const createMigratedLoan = async (migrationId, payload) => {
  try {
    const { data } = await api.post(`migrations/${migrationId}/loans/`, payload);
    return data;
  } catch (error) {
    throw new Error(parseError(error, "Failed to create migrated loan."));
  }
};

export const saveBusinessBalance = async (migrationId, payload) => {
  try {
    const { data } = await api.post(`migrations/${migrationId}/business-balance/`, payload);
    return data;
  } catch (error) {
    throw new Error(parseError(error, "Failed to save business balances."));
  }
};

export const transitionMigration = async (migrationId, status, reason) => {
  try {
    const payload = { status };
    if (reason) payload.reason = reason;
    const { data } = await api.post(`migrations/${migrationId}/transition/`, payload);
    return data;
  } catch (error) {
    throw new Error(parseError(error, "Failed to update migration status."));
  }
};

export const finalizeMigration = async (migrationId) => {
  try {
    const { data } = await api.post(`migrations/${migrationId}/finalize/`);
    return data;
  } catch (error) {
    throw new Error(parseError(error, "Failed to finalize migration."));
  }
};
